import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Card, Container, Form, Spinner, Stack } from 'react-bootstrap';
import Select, { StylesConfig } from 'react-select';
import toast from 'react-hot-toast';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import DeviceChecklistService from 'api/deviceChecklist/DeviceChecklistService';
import { TrackingItemDto } from 'api/deviceChecklist/models/TrackingItemDto';
import { Paged } from 'api/common/types/Page';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import { Reducers } from 'store/types';
import UsersService from 'api/users/UsersService';
import VehiclesService from 'api/vehicles/VehiclesService';
import CompaniesService from 'api/companies/CompaniesService';
import TrackingItemsTable from 'common/components/trackingItemsTable/TrackingItemsTable';

import styles from './TrackingItemsScreen.module.scss';

interface VehicleOption {
    value: string;
    label: string;
}

const vehicleSelectStyles: StylesConfig<VehicleOption, false> = {
    control: (base, state) => ({
        ...base,
        minHeight: '38px',
        borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
        boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
        '&:hover': { borderColor: state.isFocused ? '#86b7fe' : '#ced4da' },
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e9ecef' : 'white',
        color: state.isSelected ? 'white' : '#212529',
        cursor: 'pointer',
    }),
};

const TrackingItemsScreen: React.FC = () => {
    const { t } = useTranslation();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const isPlatformAdmin = UsersService.isPlatformAdmin(userProfile?.roles);
    const [vehicles, setVehicles] = useState<VehicleOption[]>([]);
    const [selectedVehicleId, setSelectedVehicleId] = useState('');
    const today = new Date().toISOString().split('T')[0];
    const [startDate, setStartDate] = useState(today);
    const [stopDate, setStopDate] = useState(today);
    const [trackingPage, setTrackingPage] = useState<Paged<TrackingItemDto> | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);

    React.useEffect(() => {
        if (isPlatformAdmin) {
            const vehiclesPromise = VehiclesService.getList({ page: 1, itemsPerPage: 1000 });
            const companiesPromise = CompaniesService.getCompanies();
            Promise.all([vehiclesPromise, companiesPromise])
                .then(([res, companies]) => {
                    const companyMap = new Map<string, string>();
                    for (const c of companies) {
                        if (c.value && c.label) companyMap.set(c.value, c.label);
                    }
                    const opts: VehicleOption[] = [];
                    for (const v of res.items ?? []) {
                        if (!v.id) continue;
                        const companyName = v.companyId ? companyMap.get(v.companyId) : undefined;
                        const plate = (v.licensePlate ?? '').toUpperCase().trim();
                        const designation = (v.designation ?? '').trim();
                        let label = designation ? `${plate} — ${designation}` : plate;
                        if (companyName) label += ` (${companyName})`;
                        opts.push({ value: v.id, label });
                    }
                    setVehicles(opts);
                })
                .catch(() => toast.error(t('messages.error_load_info')));
        } else {
            VehiclesService.getMyVehicles()
                .then(list => setVehicles(
                    list.filter(v => v.value && v.label).map(v => ({ value: v.value!, label: v.label! }))
                ))
                .catch(() => toast.error(t('messages.error_load_info')));
        }
    }, []);

    const loadTrackingItems = useCallback(async (vehicleId: string, start: string, stop: string, page: number = 1) => {
        if (!vehicleId) return;
        if (start && stop && start > stop) {
            setTrackingPage(null);
            return;
        }
        setLoading(true);
        try {
            const result = await DeviceChecklistService.getTrackingItems(vehicleId, start, stop, page, DEFAULT_PAGINATION_ITEMS_PER_PAGE);
            setTrackingPage(result);
            setCurrentPage(page);
        } catch {
            setTrackingPage(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleVehicleChange = (vehicleId: string) => {
        setSelectedVehicleId(vehicleId);
        setCurrentPage(1);
        if (vehicleId) {
            loadTrackingItems(vehicleId, startDate, stopDate, 1);
        } else {
            setTrackingPage(null);
        }
    };

    const handleStartDateChange = (date: string) => {
        setStartDate(date);
        setCurrentPage(1);
        if (selectedVehicleId) {
            loadTrackingItems(selectedVehicleId, date, stopDate, 1);
        }
    };

    const handleStopDateChange = (date: string) => {
        setStopDate(date);
        setCurrentPage(1);
        if (selectedVehicleId) {
            loadTrackingItems(selectedVehicleId, startDate, date, 1);
        }
    };

    const handlePageChange = (page: number) => {
        if (selectedVehicleId) {
            loadTrackingItems(selectedVehicleId, startDate, stopDate, page);
        }
    };

    return (
        <ScreenTitle title={t('tracking_items.title')}>
            <Container fluid>
                <Card className="mb-4">
                    <Card.Body>
                        <Stack direction="horizontal" gap={3}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <Select<VehicleOption, false>
                                    options={vehicles}
                                    value={vehicles.find(v => v.value === selectedVehicleId) ?? null}
                                    isClearable
                                    isSearchable
                                    placeholder={t('common.select_option')}
                                    noOptionsMessage={() => t('deviceChecklist.autocomplete_no_options')}
                                    styles={vehicleSelectStyles}
                                    menuPortalTarget={document.body}
                                    onChange={(opt) => handleVehicleChange(opt?.value ?? '')}
                                    filterOption={(option, raw) => {
                                        if (!raw) return true;
                                        return option.label.toLowerCase().includes(raw.toLowerCase());
                                    }}
                                />
                            </div>
                            <Form.Control
                                type="date"
                                value={startDate}
                                onChange={(e) => handleStartDateChange(e.target.value)}
                                aria-label={t('tracking_items.start_date')}
                                title={t('tracking_items.start_date')}
                                style={{ maxWidth: 200 }}
                            />
                            <Form.Control
                                type="date"
                                value={stopDate}
                                onChange={(e) => handleStopDateChange(e.target.value)}
                                aria-label={t('tracking_items.stop_date')}
                                title={t('tracking_items.stop_date')}
                                style={{ maxWidth: 200 }}
                            />
                        </Stack>
                    </Card.Body>
                </Card>

                {loading && (
                    <div className="text-center p-4">
                        <Spinner animation="border" />
                    </div>
                )}

                {!loading && selectedVehicleId && (!trackingPage || trackingPage.totalItems === 0) && (
                    <div className="text-center text-muted p-4">
                        {t('tracking_items.no_data')}
                    </div>
                )}

                {!loading && trackingPage && trackingPage.totalItems > 0 && (
                    <Card>
                        <Card.Body className="p-0">
                            <TrackingItemsTable
                                trackingPage={trackingPage}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                                className={styles.trackingItemsTable}
                            />
                        </Card.Body>
                    </Card>
                )}
            </Container>
        </ScreenTitle>
    );
};

export default TrackingItemsScreen;
